import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PartyPopper, X } from "lucide-react";
import BusinessApiService from "../../../../services/apiServices/BusinessApiService";
import { BusinessProfile } from "../../../../interfaces/BusinessObject";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import useScrollLock from "../../../../hooks/useScrollLock";
import "./ConfirmOnboardingModal.css";

/**
 * Owner sign-off on a page a captain and curator built for them.
 *
 * The checklist is a PROMPT, not a form: nothing is sent to the server but the
 * fact that the owner pressed the button. Its job is to make someone actually
 * open their gallery and their listings before saying they're happy — because
 * the cheapest moment to catch a missing section is now, while the captain is
 * still a phone call away, not three weeks later.
 *
 * That's also why the last item is about knowing HOW to edit rather than about
 * the page itself. An owner who can't find their own gallery will be back
 * asking the captain for things they could do themselves.
 */

interface ChecklistItem {
  id: string;
  label: string;
}

const CHECKLIST: readonly ChecklistItem[] = [
  {
    id: "listings",
    label: "Listings are created according to my requirements",
  },
  {
    id: "gallery",
    label: "Gallery is updated according to my requirements",
  },
  {
    id: "know_how",
    label:
      "I know how to edit my business, my listings and my gallery, how to add collaborators, how to update my business config, and how to take or cancel a subscription",
  },
  {
    id: "captain_number",
    label: "I have the captain's mobile number for any queries",
  },
];

interface ConfirmOnboardingModalProps {
  isOpen: boolean;
  businessId: string;
  businessName?: string;
  onClose: () => void;
  /**
   * Fired after the server confirms, with the updated BusinessDTO (from
   * mark-onboarded's `contents[0]`) so the page can drop the prompt without a
   * refetch. `null` only if the server answered with an empty envelope — the
   * caller should refetch in that case.
   */
  onConfirmed: (updatedBusiness: BusinessProfile | null) => void;
  /**
   * When true, a successful confirmation shows a "Set up autopay now" step
   * instead of closing — sign-off is what unlocks autopay for a business that
   * has no active subscription yet. The parent decides (it knows the
   * subscription state); leave off for a business that already pays.
   */
  offerAutopay?: boolean;
  /** Fired by "Set up autopay now". Only used with offerAutopay. */
  onSetupAutopay?: () => void;
}

const ConfirmOnboardingModal: React.FC<ConfirmOnboardingModalProps> = ({
  isOpen,
  businessId,
  businessName,
  onClose,
  onConfirmed,
  offerAutopay = false,
  onSetupAutopay,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const { triggerNotification } = useNotification();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useScrollLock(isOpen, overlayRef, ".confirm-onboarding-body");

  useEffect(() => {
    if (!isOpen) return;
    // Never remembered between opens. An owner who closed this to go and look
    // at their gallery should have to re-affirm what they saw, not find their
    // earlier ticks waiting.
    setChecked({});
    setIsSaving(false);
    setConfirmed(false);
  }, [isOpen]);

  const allChecked = CHECKLIST.every((item) => checked[item.id]);

  const handleConfirm = async () => {
    if (!allChecked || isSaving) return;
    setIsSaving(true);
    try {
      // BUSINESS_ALREADY_ONBOARDED (double-tap / a race with another tab) is
      // resolved as a success by the service layer, not thrown — so reaching
      // here at all means onboarding is confirmed, first call or not.
      const updatedBusiness = await BusinessApiService.markBusinessOnboarded(businessId);
      triggerNotification(
        "Thanks! Glad you're happy with it.",
        NotificationType.SUCCESS
      );
      onConfirmed(updatedBusiness);
      if (offerAutopay && onSetupAutopay) {
        setConfirmed(true);
      } else {
        onClose();
      }
    } catch (error) {
      // The service layer already turns NOT_OWNER into a permission-specific
      // message and resolves BUSINESS_ALREADY_ONBOARDED as success rather than
      // throwing, so anything landing here just needs its message surfaced.
      triggerNotification(
        error instanceof Error
          ? error.message
          : "Couldn't save that. Please try again.",
        NotificationType.ERROR
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="confirm-onboarding-overlay"
      ref={overlayRef}
      onClick={onClose}
    >
      <div
        className="confirm-onboarding-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-onboarding-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="confirm-onboarding-header">
          <div className="confirm-onboarding-heading">
            <span className="confirm-onboarding-icon" aria-hidden="true">
              <PartyPopper size={18} />
            </span>
            <div>
              <h2
                className="confirm-onboarding-title"
                id="confirm-onboarding-title"
              >
                Your page is ready
              </h2>
              <p className="confirm-onboarding-sub">
                {businessName
                  ? `${businessName} was set up by our captain and curator.`
                  : "This business was set up by our captain and curator."}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="confirm-onboarding-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <X size={19} />
          </button>
        </div>

        {confirmed ? (
          <>
            <div className="confirm-onboarding-body">
              <p className="confirm-onboarding-lede">
                All set — your page is confirmed. The last step to make it
                visible to customers is setting up autopay.
              </p>
            </div>
            <div className="confirm-onboarding-footer confirm-onboarding-footer--stack">
              <button
                type="button"
                className="confirm-onboarding-submit"
                onClick={() => {
                  onClose();
                  onSetupAutopay?.();
                }}
              >
                Set up autopay now
              </button>
              <button
                type="button"
                className="confirm-onboarding-later"
                onClick={onClose}
              >
                Maybe later
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="confirm-onboarding-body">
          <p className="confirm-onboarding-lede">
            Have a look through, then confirm each of these:
          </p>

          <div className="confirm-onboarding-list">
            {CHECKLIST.map((item) => (
              <label
                key={item.id}
                className={`confirm-onboarding-check${
                  checked[item.id] ? " confirm-onboarding-check--on" : ""
                }`}
              >
                <input
                  type="checkbox"
                  className="confirm-onboarding-box"
                  checked={!!checked[item.id]}
                  onChange={() =>
                    setChecked((prev) => ({
                      ...prev,
                      [item.id]: !prev[item.id],
                    }))
                  }
                />
                <span className="confirm-onboarding-label">{item.label}</span>
              </label>
            ))}
          </div>

          {/* The way out, stated plainly. Without this the only path forward is
              ticking boxes that aren't true. */}
          <p className="confirm-onboarding-help">
            If any of the above is missing, please contact your captain to sort
            it out.
          </p>
        </div>

        <div className="confirm-onboarding-footer">
          <button
            type="button"
            className="confirm-onboarding-submit"
            onClick={handleConfirm}
            disabled={!allChecked || isSaving}
            title={!allChecked ? "Confirm every item to continue" : undefined}
          >
            {isSaving ? "Saving..." : "I'm Happy :)"}
          </button>
        </div>
        </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ConfirmOnboardingModal;
